/**
 * 获取最新协议 Hook
 * 用于注册页面获取需要签署的协议
 */

import { useState, useEffect, useCallback } from "react";
import { agreementService } from "@/services/agreementService";
import type { Agreement } from "@/types/consent";

interface UseLatestAgreementsOptions {
  /** 区域代码 */
  regionCode?: string;
  /** 是否自动获取 */
  autoFetch?: boolean;
}

interface UseLatestAgreementsReturn {
  /** 协议列表 */
  agreements: Agreement[];
  /** 是否正在加载 */
  isLoading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 重新获取 */
  refetch: () => Promise<void>;
}

/**
 * 获取最新协议 Hook
 *
 * 功能：
 * - 获取最新发布的协议
 * - 支持按区域获取
 * - 错误处理和重试
 */
export function useLatestAgreements(
  options: UseLatestAgreementsOptions = {}
): UseLatestAgreementsReturn {
  const { regionCode, autoFetch = true } = options;
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAgreements = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await agreementService.getLatestAgreements(regionCode);
      setAgreements(result);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "获取协议失败，请稍后再试";
      setError(message);
      console.error("获取最新协议失败:", err);
    } finally {
      setIsLoading(false);
    }
  }, [regionCode]);

  useEffect(() => {
    if (autoFetch) {
      fetchAgreements();
    }
  }, [autoFetch, fetchAgreements]);

  return {
    agreements,
    isLoading,
    error,
    refetch: fetchAgreements,
  };
}

export default useLatestAgreements;
